import {
  type FocusEvent,
  type PointerEvent as ReactPointerEvent,
  type TextareaHTMLAttributes,
  useRef,
  useState,
} from "react";

export interface TextareaProps extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, "className"> {
  invalid?: boolean;
}

export function Textarea({
  invalid = false,
  rows = 3,
  onPointerDown,
  onFocus,
  onBlur,
  ...rest
}: Readonly<TextareaProps>) {
  const pointerDownRef = useRef(false);
  const [focusMethod, setFocusMethod] = useState<"pointer" | "keyboard" | null>(null);
  const isKeyboardFocus = focusMethod === "keyboard";
  const isFocused = focusMethod !== null;

  const handlePointerDown = (event: ReactPointerEvent<HTMLTextAreaElement>) => {
    pointerDownRef.current = true;
    onPointerDown?.(event);
  };

  const handleFocus = (event: FocusEvent<HTMLTextAreaElement>) => {
    setFocusMethod(pointerDownRef.current ? "pointer" : "keyboard");
    pointerDownRef.current = false;
    onFocus?.(event);
  };

  const handleBlur = (event: FocusEvent<HTMLTextAreaElement>) => {
    setFocusMethod(null);
    onBlur?.(event);
  };

  return (
    <textarea
      rows={rows}
      aria-invalid={invalid || undefined}
      onPointerDown={handlePointerDown}
      onFocus={handleFocus}
      onBlur={handleBlur}
      className={[
        "w-full min-w-0 flex-1 resize-y rounded-xl bg-surface-elevated px-4 py-2 text-sm text-content-primary placeholder:text-content-tertiary",
        "border",
        invalid ? "border-error" : isFocused ? "border-border-focus" : "border-border-input",
        isKeyboardFocus ? "input-focus-ring" : "shadow-[0_1px_1px_rgba(0,0,0,0.05)]",
        "outline-hidden transition-colors disabled:cursor-not-allowed disabled:opacity-50",
      ].join(" ")}
      {...rest}
    />
  );
}
